import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";

const parseIds = (idsString) => {
  if (!idsString.trim()) return [];
  return idsString
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
};

const AddEditSectionModal = ({ show, onHide, onSaved, section }) => {
  const isEdit = Boolean(section?.id);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mockupIdsString, setMockupIdsString] = useState("");
  const [submitLoader, setSubmitLoader] = useState(false);
  const [detailLoader, setDetailLoader] = useState(false);

  // When opening edit modal, fetch section details so we have existing mockup IDs
  useEffect(() => {
    if (!show) return;
    if (!isEdit || !section?.id) {
      setDetailLoader(false);
      setName(section?.name ?? "");
      setDescription(section?.description ?? "");
      setMockupIdsString("");
      return;
    }
    let cancelled = false;
    const loadSectionDetail = async () => {
      setDetailLoader(true);
      try {
        const result = await apiClient.get(`/featured-sections/${section.id}/`);
        if (cancelled) return;
        setDetailLoader(false);
        if (!result.ok) return;
        const data = result.data;
        setName(data.name ?? "");
        setDescription(data.description ?? "");
        const mockups = data.mockups ?? data.listings ?? [];
        const ids = data.listing_ids ?? mockups.map((m) => m?.listing_id ?? m?.listing?.id ?? m?.id).filter(Boolean);
        const idsArray = Array.isArray(ids) ? ids : [];
        setMockupIdsString(idsArray.map(String).join(", "));
      } catch (err) {
        if (!cancelled) setDetailLoader(false);
      }
    };
    loadSectionDetail();
    return () => { cancelled = true; };
  }, [show, isEdit, section?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name?.trim();
    if (!trimmedName) {
      toast.error("Please enter a section name");
      return;
    }
    const listingIds = parseIds(mockupIdsString);
    if (listingIds.length === 0) {
      toast.error("Please enter at least one mockup ID");
      return;
    }
    try {
      setSubmitLoader(true);
      const payload = {
        name: trimmedName,
        description: description?.trim() || "",
        listing_ids: listingIds,
      };
      if (isEdit) {
        const result = await apiClient.patch(`/featured-sections/${section.id}/`, payload);
        if (!result.ok) {
          handleErrors(result.data);
          setSubmitLoader(false);
          return;
        }
        toast.success("Section updated successfully");
      } else {
        const result = await apiClient.post("/featured-sections/", payload);
        if (!result.ok) {
          handleErrors(result.data);
          setSubmitLoader(false);
          return;
        }
        toast.success("Section created successfully");
      }
      setSubmitLoader(false);
      onSaved();
    } catch (error) {
      setSubmitLoader(false);
      toast.error(isEdit ? "Failed to update section" : "Failed to create section");
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="rounded-3"
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit Section" : "Add Section"}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {detailLoader ? (
            <div className="d-flex justify-content-center py-5">
              <LoadingSpinner color="#C93312" />
            </div>
          ) : (
            <>
          <div className="mb-3">
            <label className="form-label fw-semibold">Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Section name"
              required
            />
          </div>
          <div className="mb-4">
            <label className="form-label fw-semibold">Description</label>
            <textarea
              className="form-control"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Mockups</label>
            <p className="text-muted small mb-2">
              Enter mockup IDs separated by commas (e.g., 000123, 000456, 000789)
            </p>
            <p className="text-muted small mb-2">
              Note: Enter IDs exactly as they appear in the system, including any leading zeros.
            </p>
            <textarea
              className="form-control"
              rows={3}
              value={mockupIdsString}
              onChange={(e) => setMockupIdsString(e.target.value)}
              placeholder="Enter mockup IDs separated by commas..."
            />
            {mockupIdsString.trim() && (
              <small className="text-muted d-block mt-2">
                Preview: {parseIds(mockupIdsString).length} mockup(s) will be in this section
                {parseIds(mockupIdsString).length > 0 &&
                  ` (IDs: ${parseIds(mockupIdsString).join(", ")})`}
              </small>
            )}
          </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn text-light"
            style={{ backgroundColor: "#C93312" }}
            disabled={submitLoader || detailLoader}
          >
            {submitLoader ? "Saving..." : isEdit ? "Update Section" : "Add Section"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AddEditSectionModal;
